import { EMAIL_VERIFICATION_COPY } from '../consts';
import type { ApiErrorPayload, EmailVerificationErrorKind } from '../types';

export function readApiErrorPayload(payload: unknown): ApiErrorPayload {
  if (!payload || typeof payload !== 'object') return { message: '' };

  const record = payload as Record<string, unknown>;
  const errorCode = typeof record.code === 'string' ? record.code : undefined;
  const rawMessage = record.message ?? record.error;

  if (typeof rawMessage === 'string') return { code: errorCode, message: rawMessage };
  if (Array.isArray(rawMessage) && typeof rawMessage[0] === 'string') {
    return { code: errorCode, message: rawMessage[0] };
  }

  return { code: errorCode, message: '' };
}

export function isForbiddenEmailDomainError(payload: ApiErrorPayload | null | undefined): boolean {
  const errorCode = (payload?.code ?? '').toUpperCase();
  if (errorCode.includes('DOMAIN')) return true;

  const message = (payload?.message ?? '').toLowerCase();
  return message.includes('домен');
}

export function mapEmailCodeFailure(payload: ApiErrorPayload | null | undefined): {
  errorKind: EmailVerificationErrorKind;
  message: string;
} {
  const errorCode = (payload?.code ?? '').toUpperCase();
  const message = (payload?.message ?? '').trim();
  const lowered = message.toLowerCase();

  if (errorCode.includes('EXPIR') || lowered.includes('устар') || lowered.includes('expired')) {
    return { errorKind: 'expired-code', message: EMAIL_VERIFICATION_COPY.expiredCode };
  }

  if (
    errorCode.includes('INVALID') ||
    errorCode.includes('WRONG') ||
    errorCode.includes('INCORRECT') ||
    lowered.includes('неверн') ||
    lowered.includes('invalid')
  ) {
    return { errorKind: 'invalid-code', message: EMAIL_VERIFICATION_COPY.invalidCode };
  }

  if (message) return { errorKind: 'unknown', message };
  return { errorKind: 'invalid-code', message: EMAIL_VERIFICATION_COPY.invalidCode };
}

export function isEmailVerificationRefusal(input: {
  statusCode?: number;
  errorCode?: string | null;
  message?: string | null;
}): boolean {
  if (input.statusCode && input.statusCode !== 403 && input.statusCode !== 400) return false;

  const errorCode = (input.errorCode ?? '').toUpperCase();
  if (
    errorCode.includes('VERIF') ||
    errorCode.includes('EMAIL_NOT') ||
    errorCode.includes('EMAIL_UNVERIFIED')
  ) {
    return true;
  }

  const message = (input.message ?? '').toLowerCase();
  return message.includes('подтверд') && (message.includes('почт') || message.includes('email'));
}

export function classifyEmailVerificationFailure(payload: unknown): {
  errorKind: EmailVerificationErrorKind;
  message: string;
  errorCode?: string;
} {
  const parsed = readApiErrorPayload(payload);

  if (isForbiddenEmailDomainError(parsed)) {
    return {
      errorKind: 'domain',
      message: EMAIL_VERIFICATION_COPY.domainForbidden,
      errorCode: parsed.code,
    };
  }

  const codeFailure = mapEmailCodeFailure(parsed);
  return { ...codeFailure, errorCode: parsed.code };
}
